import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, Suspense, useEffect } from 'react';
import { AppProviders } from '@/components/AppProviders';
import LoadingPage from '@/components/LoadingPage';
import { ProtectedRoute } from '@/features/auth';
import { useAuth } from '@/features/auth';
import DashboardLayout from '@/layouts/DashboardLayout';
import "./styles/globals.css";
import "./styles/theme.css";
import SuperadminUnifiedPage from './features/superadmin/pages/SuperadminUnifiedPage';
import { ClientesPage } from '@/features/clientes';
import { useNavigate } from 'react-router-dom';
import { setNavigationCallback } from '@/services/api';
import { ROLES } from '@/features/auth/types';

function TimesLegacyRedirect() {
  const { state: { user } } = useAuth();
  if (user?.role === ROLES.SUPER_ADMIN) {
    return <Navigate to="/superadmin?tab=equipe&view=time" replace />;
  }
  return <Navigate to="/meu-time?tab=equipe&view=time" replace />;
}

// Auth Pages
const AuthPage = lazy(() => import('@/features/auth/pages/AuthPage'));
const CriarContaPage = lazy(() => import('@/features/auth/pages/CriarContaPage'));
const AguardandoLiberacaoPage = lazy(() => import('@/features/auth/pages/AguardandoLiberacaoPage'));
const ResetPassword = lazy(() => import('@/features/auth/pages/ResetPassword'));

// Feature Pages
const DashboardPage = lazy(() => import('@/features/dashboard/pages/DashboardPage'));

// Grupos
const GruposPage = lazy(() => import('@/features/grupos/pages/GruposPage'));
const CreateGrupoPage = lazy(() => import('@/features/grupos/pages/CreateGrupoPage'));
const EditGrupoPage = lazy(() => import('@/features/grupos/pages/EditGrupoPage'));
const GrupoCotasPage = lazy(() => import('@/features/grupos/pages/GrupoCotasPage'));
const CreateCotaPage = lazy(() => import('@/features/cotas/pages/CreateCotaPage'));

// Bens
const BensPage = lazy(() => import('@/features/bens/pages/BensPage'));
const CreateBemPage = lazy(() => import('@/features/bens/pages/CreateBemPage'));
const EditBemPage = lazy(() => import('@/features/bens/pages/EditBemPage'));

// Cotas (removido - agora acessado via grupos)

// Propostas
const PropostasComerciaisPage = lazy(() => import('@/features/propostas/pages/PropostasComerciaisPage'));
const NovaPropostaComercialPage = lazy(() => import('@/features/propostas/pages/NovaPropostaComercialPage'));
const VisualizarPropostaComercialPage = lazy(() => import('@/features/propostas/pages/VisualizarPropostaComercialPage'));
const PropostaPublicaPage = lazy(() => import('@/features/propostas/pages/PropostaPublicaPage'));

// Contratos
const ContratosPage = lazy(() => import('@/features/contratos/pages/ContratosPage'));
const NovoContratoPage = lazy(() => import('@/features/contratos/pages/NovoContratoPage'));
const EditarContratoPage = lazy(() => import('@/features/contratos/pages/EditarContratoPage'));
const DetalheContratoPage = lazy(() => import('@/features/contratos/pages/DetalheContratoPage'));
const ContratoControleQualidadePage = lazy(() => import('@/features/contratos/pages/ContratoControleQualidadePage').then(module => ({ default: module.ContratoControleQualidadePage })));
// const AssinaturaDigitalPage = lazy(() => import('@/features/contratos/pages/AssinaturaDigitalPage'));

// Formulários Inteligentes
// const VendedorContractForm = lazy(() => import('@/features/contratos/components/form/VendedorContractForm'));
const FinanceiroForm = lazy(() => import('@/features/financeiro/components/FinanceiroForm').then(module => ({ default: module.FinanceiroForm })));

// Clientes
const NovoClientePage = lazy(() => import('@/features/clientes/pages/NovoClientePage'));
const EditarClientePage = lazy(() => import('@/features/clientes/pages/EditarClientePage'));
const ClientePerfilPage = lazy(() => import('@/features/clientes/pages/ClientePerfilPage'));
const FormularioPublicoPage = lazy(() => import('@/features/clientes/pages/FormularioPublicoPage'));
const FormularioPublicoSuccessPage = lazy(() => import('@/features/clientes/pages/FormularioPublicoSuccessPage'));

// Agendamentos
const AgendamentosPage = lazy(() => import('@/features/agendamentos/pages/AgendamentosPage'));
const AgendamentoFormPage = lazy(() => import('@/features/agendamentos/pages/AgendamentoFormPage'));

// Vendas (Funil)
import { VendasHubPage } from '@/features/vendas/pages/VendasHubPage';
import { TimelineView } from '@/features/vendas/pages/TimelineView';
import { PipelineView } from '@/features/vendas/pages/PipelineView';
import { SupervisaoVendasPage } from '@/features/vendas/pages/SupervisaoVendasPage';



// Comissoes
const ComissoesPage = lazy(() => import('@/features/comissoes/pages/ComissoesPage'));

// Metas (distribuição — Representante)
const MeuTimeUnifiedPage = lazy(
  () => import('@/features/representante/pages/MeuTimeUnifiedPage')
);

// Fluxo de Caixa
import { FluxoCaixaPage } from '@/features/admin/financeiro';

// Financeiro
const FinanceiroPage = lazy(() => import('@/features/financeiro/pages/FinanceiroPage'));

// Configuração
const ConfigurationPage = lazy(() => import('@/features/configuracoes/pages/ConfiguracoesPage'));

const PosVendasPage = lazy(() => import('@/features/pos_vendas/pages/PosVendasPage'));

const RelatoriosPage = lazy(() => import('@/pages/RelatoriosPage'));

const NotFoundPage = lazy(() => import('@/features/common/pages/NotFound'));

// Configuração do QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutos
      gcTime: 1000 * 60 * 30, // 30 minutos
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
  },
});

function AppContent() {
  const navigate = useNavigate();

  useEffect(() => {
    setNavigationCallback((path) => {
      navigate(path);
    });
  }, [navigate]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/autenticacao"
        element={
          <Suspense fallback={<LoadingPage />}>
            <AuthPage />
          </Suspense>
        }
      />
      <Route
        path="/criar-conta"
        element={
          <Suspense fallback={<LoadingPage />}>
            <CriarContaPage />
          </Suspense>
        }
      />
      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<LoadingPage />}>
            <ResetPassword />
          </Suspense>
        }
      />
      <Route
        path="/aguardando-liberacao"
        element={
          <Suspense fallback={<LoadingPage />}>
            <AguardandoLiberacaoPage />
          </Suspense>
        }
      />
      <Route
        path="/formulario-publico"
        element={
          <Suspense fallback={<LoadingPage />}>
            <FormularioPublicoPage />
          </Suspense>
        }
      />
      <Route
        path="/formulario-publico/sucesso"
        element={
          <Suspense fallback={<LoadingPage />}>
            <FormularioPublicoSuccessPage />
          </Suspense>
        }
      />

      {/* Propostas Públicas - Rota Pública */}
      <Route
        path="/propostas/publicas/:uuid"
        element={
          <Suspense fallback={<LoadingPage />}>
            <PropostaPublicaPage />
          </Suspense>
        }
      />

      {/* Protected Routes — DashboardLayout verifica a sessão internamente
          (ver src/layouts/DashboardLayout.tsx) para montar a sidebar sem
          esperar a autenticação resolver. */}
      <Route
        path="/"
        element={<DashboardLayout />}
      >
        {/* Dashboard */}
        <Route
          index
          element={
            <Suspense fallback={<LoadingPage />}>
              <DashboardPage />
            </Suspense>
          }
        />

        {/* Superadmin */}
        <Route
          path="superadmin"
          element={
            <ProtectedRoute requiredPermissions={[ROLES.SUPER_ADMIN]}>
              <Suspense fallback={<LoadingPage />}>
                <SuperadminUnifiedPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Pendentes - Redirecionar para superadmin com aba de pendentes */}
        <Route
          path="usuarios/pendentes"
          element={<Navigate to="/superadmin?tab=pendentes" replace />}
        />
        {/* Redirects legados → Desempenho */}
        <Route
          path="comissoes/configuracao"
          element={<Navigate to="/superadmin?tab=desempenho" replace />}
        />
        <Route
          path="metas"
          element={<Navigate to="/superadmin?tab=desempenho" replace />}
        />

        {/* Comissões */}
        <Route
          path="comissoes"
          element={
            <ProtectedRoute requiredGranularPerms={['podeGerenciarComissoes']}>
              <Suspense fallback={<LoadingPage />}>
                <ComissoesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Fluxo de Caixa */}
        <Route
          path="financeiro/fluxo-caixa"
          element={
            <ProtectedRoute requiredPermissions={[ROLES.SUPER_ADMIN, ROLES.REPRESENTANTE]}>
              <Suspense fallback={<LoadingPage />}>
                <FluxoCaixaPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Meu Time - Gestão unificada (admin do tenant) */}
        <Route
          path="meu-time"
          element={
            <ProtectedRoute requiredPermissions={[ROLES.REPRESENTANTE, ROLES.ASSOCIADO, ROLES.SUPERVISOR]}>
              <Suspense fallback={<LoadingPage />}>
                <MeuTimeUnifiedPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="meu-time/usuarios"
          element={
            <ProtectedRoute requiredPermissions={[ROLES.REPRESENTANTE, ROLES.ASSOCIADO, ROLES.SUPERVISOR]}>
              <Suspense fallback={<LoadingPage />}>
                <MeuTimeUnifiedPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Financeiro */}
        <Route
          path="financeiro"
          element={
            <ProtectedRoute requiredPermissions={[ROLES.ASSOCIADO, ROLES.SUPER_ADMIN]}>
              <Suspense fallback={<LoadingPage />}>
                <FinanceiroPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* Relatórios */}
        <Route
          path="relatorios"
          element={
            <ProtectedRoute requiredPermissions={[ROLES.SECRETARIA, ROLES.ASSOCIADO, ROLES.SUPER_ADMIN]}>
              <Suspense fallback={<LoadingPage />}>
                <RelatoriosPage />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Configuração - Acesso Universal para todos os usuários */}
        <Route
          path="configuracoes"
          element={
            <Suspense fallback={<LoadingPage />}>
              <ConfigurationPage />
            </Suspense>
          }
        />

        {/* Propostas */}
        <Route path="propostas">
          <Route
            index
            element={
              <Suspense fallback={<LoadingPage />}>
                <PropostasComerciaisPage />
              </Suspense>
            }
          />
          <Route
            path="nova"
            element={
              <Suspense fallback={<LoadingPage />}>
                <NovaPropostaComercialPage />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<LoadingPage />}>
                <VisualizarPropostaComercialPage />
              </Suspense>
            }
          />
          <Route
            path=":id/editar"
            element={
              <Suspense fallback={<LoadingPage />}>
                <NovaPropostaComercialPage />
              </Suspense>
            }
          />
        </Route>

        {/* Agendamentos */}
        <Route path="agendamentos">
          <Route
            index
            element={
              <ProtectedRoute requiredPermissions={[ROLES.RELACIONADOR, ROLES.LIDER_VENDAS, ROLES.ASSOCIADO, ROLES.SUPERVISOR]}>
                <Suspense fallback={<LoadingPage />}>
                  <AgendamentosPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="novo"
            element={
              <ProtectedRoute requiredPermissions={[ROLES.RELACIONADOR, ROLES.LIDER_VENDAS, ROLES.ASSOCIADO, ROLES.SUPERVISOR]}>
                <Suspense fallback={<LoadingPage />}>
                  <AgendamentoFormPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path=":id/editar"
            element={
              <ProtectedRoute requiredPermissions={[ROLES.RELACIONADOR, ROLES.LIDER_VENDAS, ROLES.ASSOCIADO, ROLES.SUPERVISOR]}>
                <Suspense fallback={<LoadingPage />}>
                  <AgendamentoFormPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Contratos */}
        <Route path="contratos">
          {/* Redirecionar para a nova pipeline unificada */}
          <Route index element={<Navigate to="/vendas/pipeline?view=lista" replace />} />
          <Route
            path="novo"
            element={
              <ProtectedRoute requiredPermissions={[ROLES.LIDER_VENDAS, ROLES.ASSOCIADO, ROLES.SUPERVISOR]}>
                <Suspense fallback={<LoadingPage />}>
                  <NovoContratoPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route path=":id" element={<Navigate to="/vendas/pipeline" replace />} />
          <Route path=":id/editar" element={<Navigate to="/vendas/pipeline" replace />} />
          {/* Manter checagens e assinatura para compatibilidade */}
          <Route
            path="checagens"
            element={
              <Suspense fallback={<LoadingPage />}>
                <ContratoControleQualidadePage />
              </Suspense>
            }
          />
          <Route
            path=":id/assinatura"
            element={
              <Suspense fallback={<LoadingPage />}>
                {/* <AssinaturaDigitalPage /> */}
              </Suspense>
            }
          />
          {/* Formulários Inteligentes */}
          <Route
            path="vendedor/novo"
            element={
              <ProtectedRoute requiredPermissions={[ROLES.LIDER_VENDAS, ROLES.ASSOCIADO, ROLES.SUPERVISOR]}>
                <Suspense fallback={<LoadingPage />}>
                  <NovoContratoPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Pós-Venda */}
        <Route path="pos-venda">
          <Route
            index
            element={
              <ProtectedRoute requiredPermissions={[ROLES.POS_VENDA, ROLES.SUPER_ADMIN, ROLES.REPRESENTANTE]}>
                <Suspense fallback={<LoadingPage />}>
                  <PosVendasPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>


        {/* Financeiro - Formulário Inteligente */}
        <Route
          path="financeiro/formulario"
          element={
            <ProtectedRoute requiredPermissions={[ROLES.ASSOCIADO, ROLES.SUPER_ADMIN]}>
              <Suspense fallback={<LoadingPage />}>
                <FinanceiroForm />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Clientes */}
        <Route path="clientes">
          <Route
            index
            element={
              <Suspense fallback={<LoadingPage />}>
                <ClientesPage />
              </Suspense>
            }
          />
          <Route
            path="novo"
            element={
              <Suspense fallback={<LoadingPage />}>
                <NovoClientePage />
              </Suspense>
            }
          />
          <Route
            path=":id"
            element={
              <Suspense fallback={<LoadingPage />}>
                <ClientePerfilPage />
              </Suspense>
            }
          />
          <Route
            path=":id/editar"
            element={
              <Suspense fallback={<LoadingPage />}>
                <EditarClientePage />
              </Suspense>
            }
          />
        </Route>

        {/* Vendas - Funil de Vendas */}
        <Route path="vendas">
          <Route
            index
            element={<VendasHubPage />}
          />
          <Route
            path="timeline/:clienteId"
            element={<TimelineView />}
          />
          <Route
            path="pipeline"
            element={<PipelineView />}
          />
          <Route
            path="supervisao"
            element={
              <ProtectedRoute requiredPermissions={[ROLES.SUPERVISOR]}>
                <Suspense fallback={<LoadingPage />}>
                  <SupervisaoVendasPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>


        {/* Times (legado) → hub do perfil */}
        <Route path="times" element={<TimesLegacyRedirect />} />

        <Route path="grupos">
          <Route
            index
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarGrupos']}>
                <Suspense fallback={<LoadingPage />}>
                  <GruposPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="new"
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarGrupos']}>
                <Suspense fallback={<LoadingPage />}>
                  <CreateGrupoPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path=":id/edit"
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarGrupos']}>
                <Suspense fallback={<LoadingPage />}>
                  <EditGrupoPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path=":id/cotas"
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarGrupos']}>
                <Suspense fallback={<LoadingPage />}>
                  <GrupoCotasPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path=":id/cotas/:cotaId"
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarGrupos']}>
                <Suspense fallback={<LoadingPage />}>
                  <GrupoCotasPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path=":id/cotas/:cotaId/editar"
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarGrupos']}>
                <Suspense fallback={<LoadingPage />}>
                  <CreateCotaPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Bens */}
        <Route path="bens">
          <Route
            index
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarBens']}>
                <Suspense fallback={<LoadingPage />}>
                  <BensPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="new"
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarBens']}>
                <Suspense fallback={<LoadingPage />}>
                  <CreateBemPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path=":id/edit"
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarBens']}>
                <Suspense fallback={<LoadingPage />}>
                  <EditBemPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Cotas */}
        <Route path="cotas">
          <Route
            path="novo/:grupoId?"
            element={
              <ProtectedRoute requiredGranularPerms={['podeGerenciarGrupos']}>
                <Suspense fallback={<LoadingPage />}>
                  <CreateCotaPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

      </Route>

      {/* 404 Not Found */}
      <Route
        path="*"
        element={
          <Suspense fallback={<LoadingPage />}>
            <NotFoundPage />
          </Suspense>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProviders>
        <AppContent />
        <Toaster />
        <ReactQueryDevtools initialIsOpen={false} />
      </AppProviders>
    </QueryClientProvider>
  );
}

export default App;
